import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Wallet,
  Search,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  User,
  Calendar,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatDate';
import { toast } from 'sonner';

const PAGE_SIZE = 50;

const WalletTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [userFilter, setUserFilter] = useState('');       // applied user filter (sent to API)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [stats, setStats] = useState({
    totalCredits: 0,
    totalDebits: 0,
    totalTransactions: 0,
    activeWallets: 0
  });

  // Fetch from backend with user + date filters
  const fetchTransactions = useCallback(async (currentPage = 0) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        skip: currentPage * PAGE_SIZE,
      });
      if (userFilter) params.append('user_id', userFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`/admin/wallet/transactions?${params}`);
      setTransactions(response.data.data.transactions || []);
      setTotalCount(response.data.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching wallet transactions:', error);
      toast.error('Failed to load wallet transactions');
    } finally {
      setLoading(false);
    }
  }, [userFilter, dateFrom, dateTo]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/wallet/stats');
      if (response.data?.data) setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching wallet stats:', error);
    }
  };

  useEffect(() => {
    fetchTransactions(page);
    fetchStats();
  }, [fetchTransactions, page]);

  // Reset to page 0 when filters change
  const clearFilters = () => {
    setUserFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setFilterType('all');
    setPage(0);
  };

  const hasActiveFilters = userFilter || dateFrom || dateTo || filterType !== 'all' || searchTerm;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);

  const formatDate = (dateString) => formatDateTime(dateString);

  const isCredit = (type) => {
    const t = type?.toUpperCase();
    return t === 'CREDIT' || t === 'WALLET_LOAD' || t === 'ADMIN_CREDIT';
  };

  // Client-side search + type filter on top of server results
  const filtered = transactions.filter(tx => {
    const matchesSearch = !searchTerm ||
      tx.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === 'all' ||
      (filterType === 'credit' && isCredit(tx.transaction_type)) ||
      (filterType === 'debit' && !isCredit(tx.transaction_type));

    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-gray-200 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="h-8 w-8 text-orange-600" />
            Wallet Transactions
          </h1>
          <p className="text-gray-600 mt-1">Filter by user or date range</p>
        </div>
        <Button variant="outline" onClick={() => fetchTransactions(page)} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Credits', value: formatCurrency(stats.totalCredits), color: 'green', Icon: TrendingUp },
          { label: 'Total Debits', value: formatCurrency(stats.totalDebits), color: 'red', Icon: TrendingDown },
          { label: 'Total Transactions', value: stats.totalTransactions, color: 'blue', Icon: IndianRupee },
          { label: 'Active Wallets', value: stats.activeWallets, color: 'purple', Icon: Wallet },
        ].map(({ label, value, color, Icon }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
                </div>
                <div className={`bg-${color}-100 p-3 rounded-full`}>
                  <Icon className={`h-6 w-6 text-${color}-600`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Row 1: Date range */}
          <div className="flex flex-col md:flex-row gap-3 items-center">
            <div className="flex items-center gap-2 flex-1">
              <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600 whitespace-nowrap">From</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-gray-600 whitespace-nowrap">To</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="flex-1"
              />
            </div>
          </div>

          {/* Row 3: Search + type filter + clear */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search name, email, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'credit', 'debit'].map(type => (
                <Button
                  key={type}
                  size="sm"
                  variant={filterType === type ? 'default' : 'outline'}
                  onClick={() => setFilterType(type)}
                  className={
                    filterType === type && type === 'credit' ? 'bg-green-600 hover:bg-green-700' :
                    filterType === type && type === 'debit' ? 'bg-red-600 hover:bg-red-700' : ''
                  }
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              ))}
              {hasActiveFilters && (
                <Button size="sm" variant="outline" onClick={clearFilters} className="text-gray-500">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Active filter pills */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 pt-1">
              {userFilter && (
                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full">
                  <User className="h-3 w-3" /> User: {userFilter}
                </span>
              )}
              {dateFrom && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                  <Calendar className="h-3 w-3" /> From: {dateFrom}
                </span>
              )}
              {dateTo && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                  <Calendar className="h-3 w-3" /> To: {dateTo}
                </span>
              )}
              {filterType !== 'all' && (
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${filterType === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {filterType === 'credit' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {filterType.charAt(0).toUpperCase() + filterType.slice(1)}s only
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            Transaction History
            <span className="ml-2 text-sm font-normal text-gray-500">
              {filtered.length} shown · {totalCount} total
            </span>
          </CardTitle>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Button
                size="sm" variant="outline"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>Page {page + 1} of {totalPages}</span>
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No transactions found</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2 text-orange-500">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          {formatDate(tx.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-gray-900">{tx.user_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{tx.user_email || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {isCredit(tx.transaction_type) ? (
                            <div className="bg-green-100 p-1.5 rounded-full"><TrendingUp className="h-3 w-3 text-green-600" /></div>
                          ) : (
                            <div className="bg-red-100 p-1.5 rounded-full"><TrendingDown className="h-3 w-3 text-red-600" /></div>
                          )}
                          <Badge className={isCredit(tx.transaction_type) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {isCredit(tx.transaction_type) ? 'Credit' : 'Debit'}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {tx.description}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold whitespace-nowrap">
                        <span className={isCredit(tx.transaction_type) ? 'text-green-600' : 'text-red-600'}>
                          {isCredit(tx.transaction_type) ? '+' : '-'}{formatCurrency(tx.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(tx.balance_after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WalletTransactions;
